import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { generateRounds, harderSign, programRoundLabel, type Recognition, type RoundType } from "@heraion/shared";

import { Badge } from "../primitives/Badge";
import { Button } from "../primitives/Button";
import { MiniTrendBars, type MiniBarMark } from "../primitives/MiniTrendBars";
import type { IconName } from "../Icon/Icon";
import { ROUND_TYPE_GLYPH } from "../primitives/RoundTypeBadge";
import type { UiTheme } from "../theme";
import type { RuleWords } from "./ruleWords";
import type { V3Styles } from "./v3Styles";

// The rule line of the exercise card (#336 slice 5, brief §5.4b): «Rule ·
// <name>», the rule in words, a bar per round of what it prescribes, the
// cells by hand («✎ N by hand», glyph and words), «Realign» when there are
// some, and «Change rule». In a program in progress only the rounds not done
// count: cells by hand in a round done are said in words, with no button,
// since the rule does not write there. A row with no rule says so and stays editable
// cell by cell (§5.1). The bars repeat the words: hidden from screen readers.
// Over a bar, a shape (§5.5, never colour alone): a dot on a test round, a
// square on a deload, a ring where cells are by hand; under it, W1..Wn.
export function RuleCard({
  theme,
  styles,
  rec,
  types,
  rounds,
  words,
  onRealign,
  onChange,
  lockedThrough = -1,
}: {
  theme: UiTheme;
  styles: V3Styles;
  rec: Recognition;
  types: readonly RoundType[];
  rounds: number;
  words: RuleWords;
  onRealign: () => void;
  onChange: () => void;
  /** Rounds 0..lockedThrough were executed: their cells by hand stay. */
  lockedThrough?: number;
}) {
  const { t } = useTranslation();
  const local = useMemo(() => makeStyles(theme), [theme]);
  // One bar per round, harder is taller on every axis (RIR upside down).
  const bars = useMemo(() => {
    if (rec.kind === "manual") return [];
    const sign = harderSign(rec.rule.axis);
    const lo = sign > 0 ? 0 : 10;
    return generateRounds(rec.rule, types, rounds).map((sets) => {
      const vs = sets.map((s) => (s.value === null ? s.reps : lo + sign * s.value));
      return vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : 0;
    });
  }, [rec, types, rounds]);
  const all = rec.kind === "rule" ? rec.exceptions.length : 0;
  const hand = rec.kind === "rule" ? rec.exceptions.filter((e) => e.week > lockedThrough).length : 0;
  const handDone = all - hand;
  const marks = useMemo(() => {
    const byHand = new Set(rec.kind === "rule" ? rec.exceptions.map((e) => e.week) : []);
    // The round type's own glyph, and «by hand»'s pencil, as on the badges.
    return bars.map((_, w): MiniBarMark[] => {
      const ty = types[w];
      const glyph = ty ? (ROUND_TYPE_GLYPH as Partial<Record<string, IconName>>)[ty] : undefined;
      return [
        ...(glyph ? [glyph] : []),
        ...(byHand.has(w) ? (["edit"] as const) : []),
      ];
    });
  }, [bars, rec, types]);
  const labels = useMemo(() => bars.map((_, w) => programRoundLabel(w + 1)), [bars]);
  return (
    <View style={local.card}>
      <Text style={styles.name} accessibilityRole="header">
        {words.title(rec)}
      </Text>
      <Text style={styles.muted}>
        {rec.kind === "rule"
          ? words.sentence(rec.rule)
          : t("editorV3.rule.manualBody", { defaultValue: "No rule recognized. Every cell is yours to edit." })}
      </Text>
      {bars.length > 1 ? (
        <MiniTrendBars
          theme={theme}
          weeks={bars}
          height={20}
          barWidth={10}
          lastColor={theme.palette.ink3}
          restColor={theme.palette.ink3}
          marks={marks}
          labels={labels}
        />
      ) : null}
      {hand > 0 ? (
        <View style={local.row}>
          <Badge
            theme={theme}
            icon="edit"
            label={t("editorV3.rule.handCount", { count: hand, defaultValue: "{{count}} cells by hand" })}
          />
        </View>
      ) : null}
      {handDone > 0 ? (
        <Text style={styles.muted}>
          {t("editorV3.rule.handDone", { count: handDone, defaultValue: "{{count}} by hand in the rounds done" })}
        </Text>
      ) : null}
      <View style={local.row}>
        {hand > 0 ? (
          <Button
            theme={theme}
            variant="secondary"
            style={local.action}
            numberOfLines={2}
            hitSlop={{ top: 8, bottom: 8 }}
            label={t("editorV3.rule.realign", { defaultValue: "Realign" })}
            accessibilityHint={t("editorV3.rule.realignHint", { defaultValue: "Every cell back on the rule" })}
            onPress={onRealign}
          />
        ) : null}
        <Button
          theme={theme}
          variant="secondary"
          style={local.action}
          numberOfLines={2}
          hitSlop={{ top: 8, bottom: 8 }}
          label={t("editorV3.rule.change", { defaultValue: "Change rule" })}
          onPress={onChange}
        />
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r } = theme;
  return StyleSheet.create({
    card: {
      gap: s.s2,
      padding: s.s4,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
    },
    row: { flexDirection: "row", alignItems: "center", gap: s.s2, flexWrap: "wrap" },
    action: { flex: 1 },
  });
}
